import isEqual from 'underscore/modules/isEqual';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import { featureCollection, point, lineString } from '@turf/helpers';
import { addPolylines, removePolylines } from './polylines';
import { addChevrons, removeChevrons } from './chevrons';
import { addMarkers, removeMarkers } from './markers';
import { addLayer, addLayerEvent } from '../layers/layer_helpers';
import { createLabelLayer } from '../layers/labels';
import { closestInterpPt } from '../../trail_planner/closest_on_line';
import { addOrUpdateGeojsonSource } from '../sources/geojson_helpers';
import { getDraggableId } from './draggable';
import { isCloseToEnds } from '../point_helpers';
import { END, NODE, PLUS, START } from 'utils/constants/MarkerIds';

const trailPlannerId = 'trailPlannerMap';
const routeNodesId = `${trailPlannerId}-nodes`;
const routeNodesDragId = getDraggableId(routeNodesId);
const routePolylinesId = `${trailPlannerId}-polylines`;
const routeChevronsId = `${trailPlannerId}-chevrons`;
const newRouteNodeId = 999999999;

const styleRouteNodes = (routeNodes, message) => {
  return routeNodes.map((routeNode, i) => {
    let labelMessage;
    let iconImage;
    if (i === 0) {
      iconImage = 'map-marker-start';
    } else if (i === routeNodes.length - 1) {
      if (isEqual(routeNode.lngLat, routeNodes[0].lngLat)) {
        iconImage = 'map-marker-start-end';
      } else {
        iconImage = 'map-marker-end';
      }
    } else {
      iconImage = 'map-marker-node';
      labelMessage = message
    }
    return {
      ...routeNode,
      iconImage,
      labelMessage
    };
  });
};

const addPolylineEvents = (map, trailPlanner, pointText) => {
  // Remove new route node on mouse-out
  const onMouseLeave = () => {
    map.getCanvas().style.cursor = map.customStyle.cursor;
    addOrUpdateGeojsonSource(map, routeNodesDragId, featureCollection([]));
  };

  // Show new route node when mousing-over polylineGeojson
  // Note: Here we are reusing the routeNodesDragId source/layer for this node to reduce the need to add yet another layer
  const onMouseMove = e => {
    if (map.shiftKeyDown) {
      onMouseLeave();
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
    const polylineIdx = e.features[0].properties.idx;
    const polyline = trailPlanner.routePolylines[polylineIdx];
    const points = polyline.points;
    if (isCloseToEnds(map, e.lngLat, points, 16)) {
      addOrUpdateGeojsonSource(map, routeNodesDragId, featureCollection([]));
      return;
    }
    const closest = closestInterpPt(e.lngLat.toArray(), points);
    const closestLngLat = closest.interpolatedPoint ? closest.interpolatedPoint : points[closest.cropIndex];
    const newRouteNode = {
      id: newRouteNodeId,
      cropIdx: closest.cropIndex,
      polylineIdx,
      iconImage: 'map-marker-plus',
      addPointText: pointText
    };
    const newRouteNodeGeojson = point(closestLngLat, newRouteNode, { id: newRouteNode.id });
    addOrUpdateGeojsonSource(map, routeNodesDragId, newRouteNodeGeojson);
    if (!map.dragging) {
      addLayer(map, createLabelLayer(`${routeNodesDragId}-labels`, routeNodesDragId, 'addPointText', true));
    }
  };

  addLayerEvent(map, routePolylinesId, true, 'mousemove', onMouseMove, false);
  addLayerEvent(map, routePolylinesId, true, 'mouseleave', onMouseLeave, false);
};

const addNodeEvents = (map, trailPlanner) => {
  // Handle route node dragged to new location
  addLayerEvent(map, routeNodesId, true, 'draglayerend', e => {
    const routeNode = e.draggingFeature.properties;
    trailPlanner.routeNodeMoved(routeNode, e.lngLat.toArray());
  });
};

const addTrailPlannerOverlay = (map, trailPlanner, lineColor, labelMessage, pointText) => {
  // Draw route polylines
  const color = trailPlanner.color;
  const lineStrings = trailPlanner.routePolylines.map((routePolyline, idx) => lineString(routePolyline.points, { idx, color }));
  const polylineGeojson = featureCollection(lineStrings);
  const polylineLayerExists = map.getLayer(routePolylinesId);
  const polylineOpts = lineColor ? { 'line-color': lineColor } : null;
  addPolylines(map, routePolylinesId, polylineGeojson, {} /* sourceOpts */, polylineOpts);
  addChevrons(map, routeChevronsId, routePolylinesId);
  if (!polylineLayerExists) {
    addPolylineEvents(map, trailPlanner, pointText);
  }

  // Draw route nodes
  const routeNodes = styleRouteNodes(trailPlanner.routeNodes, labelMessage);
  const routeLayerExists = map.getLayer(routeNodesId);
  addMarkers(map, routeNodesId, routeNodes, [START, END, NODE, PLUS], null, null, true);
  if (!routeLayerExists) {
    addNodeEvents(map, trailPlanner);
  }
};

const removeTrailPlannerMap = map => {
  removeMarkers(map, routeNodesId, true);
  removeChevrons(map, routeChevronsId);
  removePolylines(map, routePolylinesId);
};

export { addTrailPlannerOverlay, removeTrailPlannerMap, routeNodesId, routeNodesDragId, newRouteNodeId };
